import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { WHEELS_DEPARTMENT_SLUG, BOLT_PATTERNS } from '../constants/marketplace';
import { getListings } from '../services/marketplace';
import MarketplaceListingCard from '../components/marketplace/MarketplaceListingCard';
import MarketplaceCategoryPicker from '../components/marketplace/MarketplaceCategoryPicker';
import MarketplaceFilterSheet from '../components/marketplace/MarketplaceFilterSheet';

const PAGE_SIZE = 20;

// Filter keys that map straight onto getListings query params. Mirrors the
// app's browse.jsx FILTER_KEYS, plus roadLegality + universalOnly which the
// web filter sheet exposes.
const FILTER_KEYS = [
  'q', 'departmentSlug', 'categorySlug', 'condition', 'partTier', 'minPrice', 'maxPrice',
  'country', 'region', 'roadLegality', 'universalOnly', 'sortBy',
  'boltPattern', 'wheelDiameter', 'wheelWidth', 'offsetMin', 'offsetMax',
  'fitMake', 'fitModel', 'fitYear',
];

// ─── Inline stroked SVG icons (Lucide / Feather style — never emojis) ───────
const SearchIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const ClearIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <line x1="15" y1="9" x2="9" y2="15" />
    <line x1="9" y1="9" x2="15" y2="15" />
  </svg>
);

const GridIcon = ({ size = 15 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="7" height="7" />
    <rect x="14" y="3" width="7" height="7" />
    <rect x="14" y="14" width="7" height="7" />
    <rect x="3" y="14" width="7" height="7" />
  </svg>
);

const SlidersIcon = ({ size = 15 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <line x1="4" y1="21" x2="4" y2="14" />
    <line x1="4" y1="10" x2="4" y2="3" />
    <line x1="12" y1="21" x2="12" y2="12" />
    <line x1="12" y1="8" x2="12" y2="3" />
    <line x1="20" y1="21" x2="20" y2="16" />
    <line x1="20" y1="12" x2="20" y2="3" />
    <line x1="1" y1="14" x2="7" y2="14" />
    <line x1="9" y1="8" x2="15" y2="8" />
    <line x1="17" y1="16" x2="23" y2="16" />
  </svg>
);

const BookmarkIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
  </svg>
);

const PlusIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const ChevronDown = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const TagsIcon = ({ size = 38 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 5H2v7l6.29 6.29c.94.94 2.48.94 3.42 0l3.58-3.58c.94-.94.94-2.48 0-3.42L9 5z" />
    <path d="M6 9.01V9" />
    <path d="M15 5l6.3 6.3a2.4 2.4 0 0 1 0 3.4L17 19" />
  </svg>
);

const CloudOff = ({ size = 38 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22.61 16.95A5 5 0 0 0 18 10h-1.26a8 8 0 0 0-7.05-6M5 5a8 8 0 0 0 4 15h9a5 5 0 0 0 1.7-.3" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest' },
  { value: 'price_asc', label: 'Price: low to high' },
  { value: 'price_desc', label: 'Price: high to low' },
  { value: 'most_saved', label: 'Most saved' },
];

// Browse landing for the web marketplace. Same query/filter/sort/pagination
// logic as app/app/marketplace/browse.jsx, rewritten for React + SCSS.
export default function Marketplace() {
  const [searchParams] = useSearchParams();

  const [filters, setFilters] = useState({
    sortBy: 'newest',
    q: searchParams.get('q') || '',
    departmentSlug: searchParams.get('departmentSlug') || '',
    categorySlug: searchParams.get('categorySlug') || '',
    categoryName: searchParams.get('categoryName') || '',
  });

  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [total, setTotal] = useState(0);

  const [showCategories, setShowCategories] = useState(searchParams.get('focus') === 'categories');
  const [showFilters, setShowFilters] = useState(false);

  const pageRef = useRef(1);

  // Build the getListings params from the active filters. Empty values are
  // skipped; categorySlug takes precedence over departmentSlug; universalOnly
  // serialises to the string 'true' the API expects.
  const buildParams = useCallback((page) => {
    const p = { page, limit: PAGE_SIZE };
    for (const k of FILTER_KEYS) {
      const v = filters[k];
      if (!v) continue;
      if (k === 'departmentSlug' && filters.categorySlug) continue; // category is more specific
      if (k === 'universalOnly') { p.universalOnly = 'true'; continue; }
      p[k] = v;
    }
    return p;
  }, [filters]);

  const load = useCallback(async (reset) => {
    if (reset) { setLoading(true); setError(false); } else { setLoadingMore(true); }
    const page = reset ? 1 : pageRef.current;
    try {
      const data = await getListings(buildParams(page));
      if (!Array.isArray(data?.listings)) {
        throw new Error('Unexpected response — is the marketplace backend running?');
      }
      const rows = data.listings;
      setListings((prev) => (reset ? rows : [...prev, ...rows]));
      setTotal(data.total || 0);
      setHasMore(rows.length === PAGE_SIZE);
      pageRef.current = page + 1;
    } catch (e) {
      if (reset) setError(true);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [buildParams]);

  // Debounced reload whenever filters change (covers search-as-you-type).
  const filtersKey = useMemo(() => JSON.stringify(filters), [filters]);
  useEffect(() => {
    const t = setTimeout(() => load(true), 300);
    return () => clearTimeout(t);
  }, [filtersKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const setFilter = (patch) => setFilters((prev) => ({ ...prev, ...patch }));

  const onCategoryPicked = (node) => {
    setFilter({
      categorySlug: node.slug,
      categoryName: node.name,
      departmentSlug: node.departmentSlug || '',
    });
  };

  const showWheels = filters.departmentSlug === WHEELS_DEPARTMENT_SLUG;

  const activeCount = [
    'condition', 'partTier', 'minPrice', 'maxPrice', 'country', 'region',
    'roadLegality', 'universalOnly', 'boltPattern', 'wheelDiameter', 'wheelWidth',
    'offsetMin', 'offsetMax',
  ].filter((k) => filters[k]).length;

  return (
    <div className="container page mp-page">
      <div className="mp-header">
        <div className="mp-header__top">
          <h1 className="mp-header__title">Marketplace</h1>
          <div className="mp-header__actions">
            <Link to="/marketplace/saved" className="mp-iconlink">
              <BookmarkIcon /> Saved
            </Link>
            <Link to="/marketplace/create" className="mp-sell-btn">
              <PlusIcon /> Sell a part
            </Link>
          </div>
        </div>

        <div className="mp-search">
          <SearchIcon />
          <input
            className="mp-search__input"
            placeholder="Search parts, brands, part numbers"
            value={filters.q || ''}
            onChange={(e) => setFilter({ q: e.target.value })}
          />
          {filters.q ? (
            <button type="button" className="mp-search__clear" onClick={() => setFilter({ q: '' })} aria-label="Clear search">
              <ClearIcon />
            </button>
          ) : null}
        </div>

        <div className="mp-toolbar">
          <button
            type="button"
            className={`mp-toolbar__btn${filters.categoryName ? ' is-active' : ''}`}
            onClick={() => setShowCategories(true)}
          >
            <GridIcon />
            <span>{filters.categoryName || 'Categories'}</span>
          </button>
          <button
            type="button"
            className={`mp-toolbar__btn${activeCount > 0 ? ' is-active' : ''}`}
            onClick={() => setShowFilters(true)}
          >
            <SlidersIcon />
            <span>Filters{activeCount > 0 ? ` (${activeCount})` : ''}</span>
          </button>
          <div className="mp-sort">
            <select
              value={filters.sortBy || 'newest'}
              onChange={(e) => setFilter({ sortBy: e.target.value })}
              aria-label="Sort by"
            >
              {SORT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <ChevronDown />
          </div>
        </div>

        {showWheels ? (
          <div className="mp-bolt-row">
            <span className="mp-bolt-row__label">PCD</span>
            {BOLT_PATTERNS.map((b) => {
              const active = filters.boltPattern === b;
              return (
                <button
                  type="button"
                  key={b}
                  className={`mp-bolt-chip${active ? ' is-active' : ''}`}
                  onClick={() => setFilter({ boltPattern: active ? '' : b })}
                >
                  {b}
                </button>
              );
            })}
          </div>
        ) : null}

        {!loading ? (
          <div className="mp-result-count">{total} {total === 1 ? 'listing' : 'listings'}</div>
        ) : null}
      </div>

      {loading ? (
        <div className="mp-state"><div className="mp-spinner" /></div>
      ) : error ? (
        <div className="mp-state mp-state--error">
          <CloudOff />
          <h3>Couldn’t load listings</h3>
          <button type="button" className="mp-retry-btn" onClick={() => load(true)}>Try again</button>
        </div>
      ) : listings.length === 0 ? (
        <div className="mp-state">
          <TagsIcon />
          <h3>No listings match</h3>
          <p>Try removing a filter or widening your search.</p>
        </div>
      ) : (
        <>
          <div className="mp-grid">
            {listings.map((item) => (
              <MarketplaceListingCard key={item.id} listing={item} onSave={() => {}} />
            ))}
          </div>
          {hasMore ? (
            <div className="mp-load-more">
              <button
                type="button"
                className="mp-load-more__btn"
                onClick={() => load(false)}
                disabled={loadingMore}
              >
                {loadingMore ? 'Loading…' : 'Load more'}
              </button>
            </div>
          ) : null}
        </>
      )}

      {showCategories ? (
        <MarketplaceCategoryPicker
          value={filters.categorySlug ? { id: filters.categorySlug, slug: filters.categorySlug, name: filters.categoryName } : null}
          onChange={onCategoryPicked}
          onClose={() => setShowCategories(false)}
        />
      ) : null}

      {showFilters ? (
        <MarketplaceFilterSheet
          value={filters}
          onApply={(next) => setFilters((prev) => ({ ...prev, ...next }))}
          onClose={() => setShowFilters(false)}
        />
      ) : null}
    </div>
  );
}
